import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Empty, Loader } from "@/components/cast/Common";
import { Emblem } from "@/components/cast/Emblem";
import { AvatarDialog } from "@/components/cast/AvatarDialog";
import { TestimonyCard } from "@/components/cast/TestimonyCard";
import { ReportDialog } from "@/components/cast/ReportDialog";
import { NarratorPicker } from "@/components/cast/NarratorPicker";
import { BibleVersionPicker } from "@/components/cast/BibleVersionPicker";
import { DeleteAccount } from "@/components/cast/DeleteAccount";
import { SignedInDevices } from "@/components/cast/SignedInDevices";

const PREFS = [
  { k: "daily_nudge", t: "Daily streak reminder", d: "An evening nudge if you haven't opened Cast that day and have a streak going." },
  { k: "plan_reminder", t: "Reading plan reminder", d: "An evening email on days you haven't read your plan yet (Cast Plus)." },
  { k: "guest_reminder", t: "Guest pass reminder", d: "A heads-up a week before your Cast Plus guest pass ends, and on its last day." },
  { k: "weekly_verse", t: "Sunday verse", d: "A verse and a story to start your week, Sundays at 7am Eastern." },
  { k: "parent_progress", t: "Weekly family progress", d: "Stories and stars your children earned in Kids Cove, every Sunday.", parent: true },
];

function EmailPrefs() {
  const { user, setUser } = useAuth();
  const prefs = user.email_prefs || {};
  const toggle = async (k: string, v: boolean) => {
    try { const { data } = await api.put("/me/email-prefs", { [k]: v }); setUser({ ...user, email_prefs: data }); toast.success(v ? "Email turned on" : "Email turned off"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="card-warm p-6 sm:p-8" data-testid="email-prefs">
      <p className="eyebrow">Email preferences</p>
      <div className="mt-4 divide-y divide-[#E2DBCE]">
        {PREFS.filter((p) => !p.parent || user.role === "parent").map((p) => (
          <label key={p.k} className="flex items-center justify-between gap-6 py-4">
            <span><span className="block font-semibold text-slate-900">{p.t}</span><span className="text-sm text-slate-500">{p.d}</span></span>
            <Switch data-testid={`email-pref-${p.k}`} checked={prefs[p.k] !== false} onCheckedChange={(v) => toggle(p.k, v)} />
          </label>
        ))}
      </div>
    </section>
  );
}

export default function Profile() {
  const { username } = useParams();
  const [u, setU] = useState<any>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    setU(null);
    api.get(`/users/${username}`).then((r) => setU(r.data)).catch((e) => setErr(errMsg(e)));
    api.get(`/testimonies?author=${username}`).then((r) => setPosts(r.data)).catch(() => {});
  }, [username]);
  if (err) return <Empty title="Member not found" />;
  if (!u) return <Loader />;
  const follow = async () => { const { data } = await api.post(`/users/${username}/follow`); setU({ ...u, is_following: data.following, followers: u.followers + (data.following ? 1 : -1) }); };
  const block = async () => { const { data } = await api.post(`/users/${username}/block`); setU({ ...u, is_blocked: data.blocked, is_following: false }); toast.success(data.blocked ? "Blocked" : "Unblocked"); };

  return (
    <div className="mx-auto max-w-3xl space-y-10">
      <div className="card-warm flex flex-col items-start gap-6 p-8 sm:flex-row sm:items-center">
        {u.is_me ? <AvatarDialog onSaved={(avatar) => setU({ ...u, avatar })} /> : <Emblem avatar={u.avatar} size={88} />}
        <div className="flex-1">
          <h1 className="font-display text-4xl font-semibold text-slate-900" data-testid="profile-username">{u.display_name}</h1>
          <p className="text-sm text-slate-500">Member since {u.joined}</p>
          <div className="mt-3 flex gap-6 text-sm" data-testid="profile-counts">
            <span><b>{u.testimonies}</b> testimonies</span><span><b data-testid="profile-followers-count">{u.followers}</b> followers</span><span><b>{u.following}</b> following</span>
          </div>
        </div>
        {!u.is_me && (
          <div className="flex flex-col items-end gap-2">
            <div className="flex gap-2">
              {!u.is_blocked && <Button data-testid="profile-follow-button" onClick={follow} className={`rounded-full ${u.is_following ? "bg-white text-slate-800 ring-1 ring-[#E2DBCE] hover:bg-[#F3ECE0]" : "bg-[#1E293B]"}`}>{u.is_following ? "Following" : "Follow"}</Button>}
              <Button data-testid="profile-block-button" variant="ghost" onClick={block}>{u.is_blocked ? "Unblock" : "Block"}</Button>
            </div>
            <ReportDialog targetType="user" targetId={u.id} testId="profile-report-button" />
          </div>
        )}
      </div>
      {u.is_me && <BibleVersionPicker />}
      {u.is_me && <NarratorPicker />}
      {u.is_me && <EmailPrefs />}
      {u.is_me && <SignedInDevices />}
      {u.is_me && <DeleteAccount />}
      <div className="space-y-6">
        {u.is_blocked ? <Empty title="You've blocked this member" body="Their testimonies are hidden from you." /> : posts.length === 0 ? <Empty title="No testimonies shared yet" /> : posts.map((p) => <TestimonyCard key={p.id} post={p} onRemoved={(id) => setPosts(posts.filter((x) => x.id !== id))} />)}
      </div>
    </div>
  );
}
